import { Component, OnDestroy, OnInit, Renderer2, effect, inject, ViewChild, AfterViewInit } from '@angular/core'
import { MenuItem } from 'primeng/api'
import { BaseComponent } from '../../components/base.component'
import { AppEventService, EVENT_TYPE } from '../../services/app-event.service'
import { filter } from 'rxjs'
import { UserWelcomeResponse } from '../../models/Login'
import { MenuService } from '../../services/menu.service'
import { AuthService } from '../../services/auth.service'
import { UserStateService } from '../../store/state/user/user.state.service'
import { ZendeskService } from '../../services/zendesk.service'
import { EnvironmentService } from '../../services/environment.service'
import { SignalRHubService } from '../../services/signalR.hub.service'
import { ActivatedRoute, NavigationEnd, NavigationStart } from '@angular/router'
import { QuantumMetricService } from '../../services/quantum-metric.service'
import { AnalyticsService } from '../../services/analytics/analytics.service'
import { SessionTimeoutComponent } from '../../components/sesion-timeout/sesion-timeout.component'

@Component({
  selector: 'app-layout',
  templateUrl: './app-layout.component.html',
  styleUrl: './app-layout.component.scss',
  standalone: false
})
export class AppLayoutComponent extends BaseComponent implements OnInit, OnDestroy, AfterViewInit {

  #menuService = inject(MenuService)
  #authService = inject(AuthService)
  #userStateService = inject(UserStateService)
  #eventService = inject(AppEventService)
  #renderer = inject(Renderer2)
  #zendeskService: ZendeskService = inject(ZendeskService)
  #signalRHubService = inject(SignalRHubService)
  #quantumMetricService = inject(QuantumMetricService)
  #analyticsService = inject(AnalyticsService)
  #activatedRoute: ActivatedRoute = inject(ActivatedRoute)
  #environmentService = inject(EnvironmentService)

  @ViewChild('sessionTimeout') sessionTimeout: SessionTimeoutComponent;

  menuItems: MenuItem[]
  isLoggedIn: boolean;
  eventService: AppEventService = inject(AppEventService)
  isMenuCollapsed = true;
  environment = this.#environmentService.env;


  constructor() {
    super()
    effect(() => {
      this.isLoggedIn = this.#userStateService.getIsLoggedIn();
      if (this.isLoggedIn) {
        this.initLoggedIn()
      } else {
        this.resetLoggedIn()
      }
    })
    effect(() => {
      this.isLoggedIn = this.#userStateService.getIsLoggedIn()
      const userWelcomeInfo = this.#userStateService.getUserWelcomeInfo();
      // Build dynamic menu when user info is available
      if (userWelcomeInfo?.Dashboard) {
        this.#menuService.updateAnalyticsMenu(userWelcomeInfo.Dashboard, userWelcomeInfo?.Modules || []);
      } else {
        // console.log('No dashboard data found');
      }

      this.#eventService.raiseEventOf<UserWelcomeResponse>(EVENT_TYPE.PROFILE, userWelcomeInfo);
      this.menuItems = this.#menuService.getMenu()

      if (this.isLoggedIn) {
        this.loadJsScript()
      } else {
        this.removeZenDesk()
      }
    })
  }

  ngAfterViewInit(): void {
    if (this.sessionTimeout) {
      this.sessionTimeout.startWatching();
    }
  }

  resetLoggedIn() {
    this.#signalRHubService.closeConnection().finally();
    this.#quantumMetricService.unload();
  }

  initLoggedIn() {
    this.#signalRHubService.initSignalR();
    this.subscriptions.add(this.#signalRHubService.startConnection().subscribe());

    // Also check on initial load in case the user lands directly on a QM-enabled route
    this.#handleQuantumMetric();
  }

  #handleQuantumMetric(): void {
    const data = this.#deepestRouteData();

    if (data?.['quantumMetricAllow'] === true && data?.['quantumMetricBlock'] === true) {
      console.warn('QuantumMetric: route has both quantumMetricAllow and quantumMetricBlock set in order-step-metadata.ts file. If page needs to be blocked for privacy, add quantumMetricBlock as true. If page needs to be captured, add quantumMetricAllow as true.');
    }

    let enabled = false;
    if (data?.['quantumMetricAllow'] === true) enabled = true;
    if (data?.['quantumMetricBlock'] === true) enabled = false;

    if (enabled) {
      this.#quantumMetricService.load();
    } else {
      this.#quantumMetricService.unload();
    }
  }

  #deepestRouteData(): Record<string, unknown> | undefined {
    let route = this.#activatedRoute.snapshot;
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route.routeConfig?.data;
  }

  ngOnInit(): void {

    this.subscriptions.add(
      this.eventService.dataEvent$
        .pipe(filter(x => x.event == EVENT_TYPE.MENU_COLLAPSED))
        .subscribe({
          next: (data) => {
            this.isMenuCollapsed = data.data as boolean;
          }
        }));

    this.subscriptions.add(this.eventService.dataEvent$
      .pipe(filter(x => x.event == EVENT_TYPE.PROFILE && x.data != undefined)).subscribe({
        next: (data) => {
          (data.data as UserWelcomeResponse).Modules
        }
      }))

    this.subscriptions.add(this.#eventService.event$.
      pipe(filter(x => [EVENT_TYPE.LOGOUT, EVENT_TYPE.SESSION_EXPIRED].includes(x))).subscribe({
        next: (event) => {
          const extras = event == EVENT_TYPE.SESSION_EXPIRED ? { state: { reason: 'expired' } } : undefined;
          this.router.navigate(['/logout'], extras).then((value) => {
            if (value) {
              this.isLoggedIn = false
            }
          });
        }
      }))

    this.subscriptions.add(
      this.router.events.subscribe(event => {
        // Default-deny: pause capture the instant any navigation begins, before
        // the next component renders. Closes the race where an allowed page is
        // still opted-in while a blocked page (credit-check/payment) paints its
        // DOM. NavigationEnd then re-opts-in only if the landed page is allowed.
        if (event instanceof NavigationStart) {
          this.#quantumMetricService.unload();
        }
        if (event instanceof NavigationEnd) {
          this.#analyticsService.logPage(event.urlAfterRedirects);
          this.#handleQuantumMetric();
        }
      })
    );
  }

  loadJsScript() {
    const zenDeskNode = document.getElementById('ze-snippet');
    if (zenDeskNode && typeof (window as any).zE === 'function') {
      (window as any).zE('messenger', 'show');
    }
    else if (!zenDeskNode) {
      const node = document.createElement('script')
      node.id = 'ze-snippet'
      node.type = 'text/javascript'
      node.src = 'https://static.zdassets.com/ekr/snippet.js?key=479544e7-d73e-4bd9-8e67-4fc662313259'
      node.crossOrigin = 'anonymous'
      node.onerror = (error) => {
        console.error('Failed to load Zendesk script:', error);
      };
      this.#renderer.appendChild(document.body, node)
    }

    this.#zendeskService.initializeZendesk();
  }

  removeZenDesk(): void {
    const zenDeskNode = document.getElementById('ze-snippet');
    if (zenDeskNode && typeof (window as any).zE === 'function') {
      (window as any).zE('messenger', 'hide');
    }
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.removeZenDesk();
    this.#signalRHubService.closeConnection().finally();
    this.#quantumMetricService.unload();
    this.subscriptions.unsubscribe();
  }

  src_url = '../../../../assets/terms-privacy.pdf'

  privacyTerms = false

  //  userStateService: UserStateService = inject(UserStateService)

  // onUserSwitch(event: InputSwitchChangeEvent) {
  //   this.userStateService.setUserAuthorized(event.checked)
  // }

  showPivacyTerms() {
    this.privacyTerms = true
  }

  onSessionExpired() {
    this.#eventService.raiseEvent(EVENT_TYPE.SESSION_EXPIRED)
  }

  onSessionresume() {
    this.#authService.refreshSession();
  }


}
